import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tasksApi } from '../services/tasks';
import { logout } from './authSlice';

// ===== THUNKS =====

export const fetchTasks = createAsyncThunk(
    'tasks/fetchList',
    async ({ skip = 0, limit = 30, filters = {} }, { rejectWithValue }) => {
        try {
            return await tasksApi.getTasks(skip, limit, filters);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchTasksByCandidate = createAsyncThunk(
    'tasks/fetchByCandidate',
    async ({ candidateId, skip = 0, limit = 100 }, { rejectWithValue }) => {
        try {
            return await tasksApi.getTasksByCandidate(candidateId, skip, limit);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchTaskHistory = createAsyncThunk(
    'tasks/fetchHistory',
    async ({ taskId }, { rejectWithValue }) => {
        try {
            return await tasksApi.getTaskHistory(taskId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchTaskById = createAsyncThunk(
    'tasks/fetchById',
    async (id, { rejectWithValue }) => {
        try {
            return await tasksApi.getTaskById(id);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const createTask = createAsyncThunk(
    'tasks/create',
    async ({ task }, { rejectWithValue }) => {
        try {
            return await tasksApi.createTask(task);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateTask = createAsyncThunk(
    'tasks/update',
    async ({ id, task }, { rejectWithValue }) => {
        try {
            return await tasksApi.updateTask(id, task);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const deleteTask = createAsyncThunk(
    'tasks/delete',
    async ({ id, organisationId }, { rejectWithValue }) => {
        try {
            return await tasksApi.delete(id, organisationId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const moveTask = createAsyncThunk(
    'tasks/move',
    async ({ id, organisationId, status }, { rejectWithValue }) => {
        try {
            return await tasksApi.move(id, organisationId, status);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateTaskStatus = createAsyncThunk(
    'tasks/updateStatus',
    async ({ id, organisationId, status }, { rejectWithValue }) => {
        try {
            return await tasksApi.updateTaskStatus(id, status);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

export const assignTask = createAsyncThunk(
    'tasks/assign',
    async ({ id, organisationId, userId }, { rejectWithValue }) => {
        try {
            return await tasksApi.assign(id, organisationId, userId);
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

// ===== SLICE =====

const initialState = {
    list: [],
    current: null,
    candidateTasks: [],
    taskHistory: null,
    isLoading: false,
    error: null,
    totalPages: null,
    totalItems: null
};

const tasksSlice = createSlice({
    name: 'tasks',
    initialState,
    reducers: {
        clearCurrent: (state) => {
            state.current = null;
            state.error = null;
        },
        clearCandidateTasks: (state) => {
            state.candidateTasks = [];
        },
        updateTaskStatusOptimistic: (state, action) => {
            const { id, status } = action.payload;
            const task = state.list.find(t => t.id === id);
            if (task) {
                task.status = status;
            }
            // Также обновляем в candidateTasks если задача там есть
            const candidateTask = state.candidateTasks.find(t => t.id === id);
            if (candidateTask) {
                candidateTask.status = status;
            }
        },
        revertTaskStatus: (state, action) => {
            const { id, originalStatus } = action.payload;
            const task = state.list.find(t => t.id === id);
            if (task) {
                task.status = originalStatus;
            }
            // Также обновляем в candidateTasks если задача там есть
            const candidateTask = state.candidateTasks.find(t => t.id === id);
            if (candidateTask) {
                candidateTask.status = originalStatus;
            }
        }
    },
    extraReducers: (builder) => {
        builder

            // Get List
            .addCase(fetchTasks.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchTasks.fulfilled, (state, action) => {
                state.isLoading = false;
                state.list = action.payload.items || action.payload;
                state.totalPages = action.payload.total_pages || null;
                state.totalItems = action.payload.total_items || null;
            })
            .addCase(fetchTasks.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get by Candidate
            .addCase(fetchTasksByCandidate.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchTasksByCandidate.fulfilled, (state, action) => {
                state.isLoading = false;
                state.candidateTasks = action.payload;
            })
            .addCase(fetchTasksByCandidate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get Task History
            .addCase(fetchTaskHistory.pending, (state) => {
                // state.isLoading = true;
            })
            .addCase(fetchTaskHistory.fulfilled, (state, action) => {
                state.isLoading = false;
                state.taskHistory = action.payload;
            })
            .addCase(fetchTaskHistory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get by ID
            .addCase(fetchTaskById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchTaskById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.current = action.payload.data;
                const index = state.list.findIndex(item => item.id === action.payload.data.id);
                if (index !== -1) state.list[index] = action.payload.data;
            })
            .addCase(fetchTaskById.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Create
            .addCase(createTask.fulfilled, (state, action) => {
                state.list.push(action.payload);
                state.candidateTasks.push(action.payload);
            })

            // Update
            .addCase(updateTask.fulfilled, (state, action) => {
                const index = state.list.findIndex(item => item.id === action.payload.id);
                if (index !== -1) state.list[index] = action.payload;

                // Обновляем задачу в candidateTasks если она там есть
                const candidateIndex = state.candidateTasks.findIndex(item => item.id === action.payload.id);
                if (candidateIndex !== -1) state.candidateTasks[candidateIndex] = action.payload;
            })

            // Delete
            .addCase(deleteTask.fulfilled, (state, action) => {
                state.list = state.list.filter(item => item.id !== action.meta.arg.id);
            })

            // Move
            .addCase(moveTask.fulfilled, (state, action) => {
                const index = state.list.findIndex(item => item.id === action.payload.id);
                if (index !== -1) state.list[index] = action.payload;
            })

            // Assign
            .addCase(assignTask.fulfilled, (state, action) => {
                const index = state.list.findIndex(item => item.id === action.payload.id);
                if (index !== -1) state.list[index] = action.payload;
            })

            // Update Status
            .addCase(updateTaskStatus.fulfilled, (state, action) => {
                const index = state.list.findIndex(item => item.id === action.payload.id);
                if (index !== -1) state.list[index] = action.payload;

                // Также обновляем в candidateTasks если задача там есть
                const candidateIndex = state.candidateTasks.findIndex(item => item.id === action.payload.id);
                if (candidateIndex !== -1) state.candidateTasks[candidateIndex] = action.payload;
            })
            // Обработка logout из authSlice
            .addCase(logout, (state) => {
                state.list = [];
                state.candidateTasks = [];
                state.current = null;
                state.history = [];
                state.isLoading = false;
                state.error = null;
            });
    }
});

export const { clearCurrent, clearCandidateTasks, updateTaskStatusOptimistic, revertTaskStatus } = tasksSlice.actions;
export const tasksReducer = tasksSlice.reducer; 